/**
 * Core Layer - HTTP Client
 * Framework-agnostic Axios instance for API calls
 * Does NOT contain React-specific code
 */

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { siteConfig } from "@/config/site";
import { COOKIE_NAMES, HTTP_HEADERS, MIME_TYPES } from "@/constants";
import { EndpointType } from "../types";
import { storageClient } from "./storageClient";

/**
 * Get base URL based on endpoint type
 */
const getBaseUrl = (endpointType: EndpointType): string => {
  switch (endpointType) {
    case EndpointType.APP_API:
      return `/${siteConfig.apiMainPath}/${siteConfig.apiVersion}`;
    case EndpointType.DIRECT_API:
      return `${siteConfig.apiBaseUrl}/${
        siteConfig.apiMainPath ? `${siteConfig.apiMainPath}/` : ""
      }${siteConfig.apiVersion}`;
    default:
      return `${siteConfig.apiBaseUrl}/${siteConfig.apiMainPath}/${siteConfig.apiVersion}`;
  }
};

/**
 * Request interceptor handler
 */
const requestInterceptor = (
  config: InternalAxiosRequestConfig,
  endpointType: EndpointType
): InternalAxiosRequestConfig => {
  // Check network connectivity (client-side only)
  if (typeof window !== "undefined" && !window.navigator.onLine) {
    throw new Error("No internet connection");
  }

  // Add authorization token
  const token = storageClient.getCookie(COOKIE_NAMES.AUTH_TOKEN);
  if (token) {
    config.headers[HTTP_HEADERS.AUTHORIZATION] = token;
  }

  // Add API key to headers only if it's provided and not empty
  if (siteConfig.apiKey && siteConfig.apiKey.trim().length > 0) {
    config.headers[HTTP_HEADERS.API_KEY] = siteConfig.apiKey;
  }

  // Log request (enabled in dev or when NEXT_PUBLIC_ENABLE_LOGGING=true)
  if (!siteConfig.isProduction || siteConfig.enableLogging) {
    const fullUrl = config.baseURL
      ? `${config.baseURL}${config.url}`
      : config.url;
    console.group("🚀 API Request");
    console.log("📍 Full URL:", fullUrl);
    console.log("🔧 Method:", config.method?.toUpperCase());
    if (config.params) console.log("🔎 Params:", config.params);
    if (config.data) console.log("📤 Body:", config.data);
    console.groupEnd();
  }

  return config;
};

/**
 * Response success interceptor handler
 */
const responseSuccessInterceptor = (response: AxiosResponse): AxiosResponse => {
  // Log response (enabled in dev or when NEXT_PUBLIC_ENABLE_LOGGING=true)
  if (!siteConfig.isProduction || siteConfig.enableLogging) {
    const fullUrl = response.config.baseURL
      ? `${response.config.baseURL}${response.config.url}`
      : response.config.url;
    console.group("✅ API Success");
    console.log("📍 Full URL:", fullUrl);
    console.log("🔢 Status:", response.status);
    console.log("📦 Response Data:", response.data);
    console.groupEnd();
  }
  return response;
};

/**
 * Auth endpoints that should NOT trigger 401 redirect
 * (to prevent infinite redirect loops)
 */
const AUTH_ENDPOINTS = ["/auth/login", "/auth/refresh", "/users/add"];

/**
 * Check if a request URL is an auth endpoint
 */
const isAuthEndpoint = (url?: string): boolean => {
  if (!url) return false;
  return AUTH_ENDPOINTS.some((ep) => url.includes(ep));
};

/**
 * Response error interceptor handler
 * Includes global 401 safety net: clears session and redirects to login
 */
const responseErrorInterceptor = (error: unknown): Promise<never> => {
  // --- Global 401 Safety Net ---
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    // Only redirect for non-auth endpoints to prevent loops
    if (!isAuthEndpoint(error.config?.url)) {
      if (typeof window !== "undefined") {
        // Clear all auth cookies
        storageClient.deleteCookie(COOKIE_NAMES.AUTH_TOKEN);
        storageClient.deleteCookie(COOKIE_NAMES.REFRESH_TOKEN);
        storageClient.deleteCookie(COOKIE_NAMES.USER_DATA);

        // Hard redirect to login (outside React context)
        window.location.href = "/auth/login";
      }
    }
  }

  // Log errors (enabled in dev or when NEXT_PUBLIC_ENABLE_LOGGING=true)
  if (!siteConfig.isProduction || siteConfig.enableLogging) {
    console.group("🔴 API Error");

    if (axios.isAxiosError(error)) {
      const fullUrl = error.config?.baseURL
        ? `${error.config.baseURL}${error.config.url}`
        : error.config?.url;
      console.log("📍 Full URL:", fullUrl ?? "Unknown URL");
      console.log(
        "🔧 Method:",
        error.config?.method?.toUpperCase() ?? "Unknown"
      );
      console.log(
        "🔢 Status:",
        error.response?.status ?? "No response (network error)"
      );
      console.log(
        "📦 Response Data:",
        error.response?.data ?? "No response data"
      );
      console.log("💬 Error Message:", error.message);

      // Log request body if present
      if (error.config?.data) {
        try {
          const requestData =
            typeof error.config.data === "string"
              ? JSON.parse(error.config.data)
              : error.config.data;
          console.log("📤 Request Body:", requestData);
        } catch {
          console.log("📤 Request Body:", error.config.data);
        }
      }
    } else if (error instanceof Error) {
      console.log("💬 Error Message:", error.message);
      console.log("📚 Stack:", error.stack);
    } else {
      console.log("❓ Unknown Error:", error);
    }

    console.groupEnd();
  }
  return Promise.reject(error);
};


/**
 * Create Axios instance with interceptors
 */
const createAxiosInstance = (
  endpointType: EndpointType = siteConfig.defaultEndpointType
): AxiosInstance => {
  const baseURL = getBaseUrl(endpointType);

  const instance = axios.create({
    baseURL,
    headers: {
      [HTTP_HEADERS.CONTENT_TYPE]: MIME_TYPES.JSON,
      [HTTP_HEADERS.ACCEPT]: MIME_TYPES.JSON,
    },
    timeout: siteConfig.apiTimeout,
  });

  // Add interceptors
  instance.interceptors.request.use(
    (config) => requestInterceptor(config, endpointType),
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    responseSuccessInterceptor,
    responseErrorInterceptor
  );

  return instance;
};

/**
 * Get Axios instance for endpoint type
 */
const getAxiosInstance = (
  endpointType: EndpointType = siteConfig.defaultEndpointType
): AxiosInstance => {
  return createAxiosInstance(endpointType);
};

/**
 * Extended AxiosRequestConfig with endpoint type
 */
export interface HttpClientConfig extends AxiosRequestConfig {
  endpointType?: EndpointType;
}

/**
 * HTTP Client - Type-safe API methods
 * Framework-agnostic wrapper around Axios
 */
export const httpClient = {
  get: <T = unknown>(
    url: string,
    config?: HttpClientConfig
  ): Promise<AxiosResponse<T>> => {
    const { endpointType, ...restConfig } = config || {};
    return getAxiosInstance(endpointType).get<T>(url, restConfig);
  },

  post: <T = unknown>(
    url: string,
    data?: unknown,
    config?: HttpClientConfig
  ): Promise<AxiosResponse<T>> => {
    const { endpointType, ...restConfig } = config || {};
    return getAxiosInstance(endpointType).post<T>(url, data, restConfig);
  },

  put: <T = unknown>(
    url: string,
    data?: unknown,
    config?: HttpClientConfig
  ): Promise<AxiosResponse<T>> => {
    const { endpointType, ...restConfig } = config || {};
    return getAxiosInstance(endpointType).put<T>(url, data, restConfig);
  },

  patch: <T = unknown>(
    url: string,
    data?: unknown,
    config?: HttpClientConfig
  ): Promise<AxiosResponse<T>> => {
    const { endpointType, ...restConfig } = config || {};
    return getAxiosInstance(endpointType).patch<T>(url, data, restConfig);
  },

  delete: <T = unknown>(
    url: string,
    config?: HttpClientConfig
  ): Promise<AxiosResponse<T>> => {
    const { endpointType, ...restConfig } = config || {};
    return getAxiosInstance(endpointType).delete<T>(url, restConfig);
  },
};

export default httpClient;
